import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { useId } from 'react';
import { getDisplayName } from '../../../../../../models/NameUtils';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Button } from '../../../../../components-v2/Button';
import { Checkbox } from '../../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../../../components-v2/Combobox';
import { UserComboboxAvatarItem } from '../../../../../components/UserCombobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components-v2/Select';
import { bs } from '../../../../../styles/styles';
import type { OpportunityReportingNormalizedFilters } from '../../helpers/opportunityReportingUrlFilters';
import type { UseOpportunityReportingFilterBarReturn } from '../../hooks/useOpportunityReportingFilterBar';
import { styleSheet } from '../../styles';
import { DateRangePicker } from '../../../../../components-v2/DateRangePicker';

interface FilterBarProps {
	stages: Api.IBoardStage[] | undefined;
	isBoardInvalid: boolean;
	filterBar: UseOpportunityReportingFilterBarReturn;
}

export const FilterBar: React.FC<FilterBarProps> = ({ stages, isBoardInvalid, filterBar }) => {
	const includeArchivedId = useId();

	const {
		filterStageId,
		ownerScope,
		creationDateFrom,
		creationDateTo,
		closeDateFrom,
		closeDateTo,
		includeArchived,
		hasActiveReportFilters,
		ownerSearchFragment,
		setOwnerSearchFragment,
		ownerComboboxValue,
		ownerComboboxItems,
		ownerAutocompleteQuery,
		patchFilters,
		onOwnerScopeChange,
		handleOwnerUserSelected,
		handleClearReportFilters,
	} = filterBar;

	const stageSelectItems = React.useMemo(
		() => stages?.map(s => ({ label: s.name ?? s.id ?? '', value: s.id ?? '' })) ?? [],
		[stages]
	);

	const ownerSearchStatus = React.useMemo(() => {
		if (!ownerSearchFragment) {
			return 'Start typing to search employees…';
		}
		if (ownerAutocompleteQuery.isLoading) {
			return (
				<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
					<LoadingSpinner type='extra-tiny' />
					Searching users…
				</div>
			);
		}
		if (ownerAutocompleteQuery.error) {
			return 'Failed to search employees. Please try again.';
		}
		if (ownerComboboxItems.length === 0) {
			return `No users found for "${ownerSearchFragment}"`;
		}
		return `${ownerComboboxItems.length} user${ownerComboboxItems.length === 1 ? '' : 's'} found`;
	}, [ownerSearchFragment, ownerAutocompleteQuery.isLoading, ownerAutocompleteQuery.error, ownerComboboxItems.length]);

	return (
		<div className={css(styleSheet.reportFilters)}>
			{stageSelectItems.length > 0 ? (
				<div className={css(styleSheet.reportFilterField)}>
					<label className={css(styleSheet.label)} htmlFor='op-adv-stage'>
						Stage
					</label>
					<Select
						items={stageSelectItems}
						value={filterStageId}
						onValueChange={v => patchFilters({ filterStageId: v })}
					>
						<SelectTrigger id='op-adv-stage' styles={[styleSheet.select]}>
							<SelectValue placeholder='All stages' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='' label='All stages'>
								All stages
							</SelectItem>
							{stageSelectItems.map(s => (
								<SelectItem key={s.value} value={s.value} label={s.label}>
									{s.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			) : null}
			<div className={css(styleSheet.ownerFilterContainer)}>
				<label className={css(styleSheet.label)} htmlFor='op-adv-owner-scope'>
					Owner
				</label>
				<div className={css(styleSheet.ownerFilterRow)}>
					<div
						className={css(
							styleSheet.ownerScopeField,
							ownerScope === 'specific' ? styleSheet.ownerScopeFieldSpecific : undefined
						)}
					>
						<Select
							items={[
								{ label: 'All', value: 'any' },
								{ label: 'Unassigned', value: 'unassigned' },
								{ label: 'Specific user', value: 'specific' },
							]}
							value={ownerScope}
							onValueChange={v => onOwnerScopeChange(v as OpportunityReportingNormalizedFilters['ownerScope'])}
						>
							<SelectTrigger id='op-adv-owner-scope' styles={[styleSheet.select]}>
								<SelectValue placeholder='All' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='any' label='All'>
									All
								</SelectItem>
								<SelectItem value='unassigned' label='Unassigned'>
									Unassigned
								</SelectItem>
								<SelectItem value='specific' label='Specific user'>
									Specific user
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{ownerScope === 'specific' ? (
						<div className={css(styleSheet.ownerComboboxWrap)}>
							<Combobox
								items={ownerComboboxItems}
								value={ownerComboboxValue}
								onValueChange={handleOwnerUserSelected}
								inputValue={ownerSearchFragment}
								onInputValueChange={setOwnerSearchFragment}
								filter={null}
								itemToStringLabel={(user: Api.IUser) => getDisplayName(user)}
								isItemEqualToValue={(item: Api.IUser, value: Api.IUser) => item.id === value.id}
							>
								<ComboboxInput
									placeholder='Search employee…'
									showClear
									aria-label='Filter by owner'
									styles={[styleSheet.ownerComboboxInput]}
								/>
								<ComboboxContent>
									<ComboboxStatus>{ownerSearchStatus}</ComboboxStatus>
									<ComboboxList>
										{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						</div>
					) : null}
				</div>
				{isBoardInvalid ? (
					<p className={css(styleSheet.errorNote)}>This board could not be loaded. Pick another from the list.</p>
				) : null}
			</div>
			<div className={css(styleSheet.reportFilterField)}>
				<span className={css(styleSheet.label)}>Creation date</span>
				<DateRangePicker
					size='sm'
					ariaLabel='Creation date range'
					from={creationDateFrom}
					to={creationDateTo}
					onChange={(from, to) => patchFilters({ creationDateFrom: from, creationDateTo: to })}
				/>
			</div>
			<div className={css(styleSheet.reportFilterField)}>
				<span className={css(styleSheet.label)}>Close date</span>
				<DateRangePicker
					size='sm'
					ariaLabel='Close date range'
					from={closeDateFrom}
					to={closeDateTo}
					onChange={(from, to) => patchFilters({ closeDateFrom: from, closeDateTo: to })}
				/>
			</div>
			<div className={css(styleSheet.reportFilterField)}>
				<span className={css(styleSheet.label)} aria-hidden='true'>
					&nbsp;
				</span>
				<label className={css(styleSheet.checkboxLabel)} htmlFor={includeArchivedId}>
					<Checkbox
						id={includeArchivedId}
						checked={includeArchived}
						onCheckedChange={v => patchFilters({ includeArchived: v === true })}
					/>
					<span>Include archived</span>
				</label>
			</div>
			{hasActiveReportFilters ? (
				<div className={css(styleSheet.clearReportFilters)}>
					<Button type='button' variant='secondary' onClick={handleClearReportFilters}>
						Clear filters
					</Button>
				</div>
			) : null}
		</div>
	);
};
